import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { findCityBySlug, cities, cityIntro, photographerMatchesCity, type CityInfo } from "@/data/cities";
import { themes, type Photographer } from "@/data/photographers";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";

export const Route = createFileRoute("/fotograf/$city")({
  loader: ({ params }) => {
    const city = findCityBySlug(params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData, params }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "Fotograf, OM Selected" }] };
    const title = `Fotograf ${city.name} finden, OM Selected`;
    const description = `Kuratierte Fotografen in ${city.name} (${city.country}) für Portrait, Hochzeit, Produkt und freie Arbeiten. Redaktionell empfohlen von OM Selected.`;
    const url = `https://omselected.art/fotograf/${params.city}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            about: {
              "@type": "Place",
              name: city.name,
              address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: city.country },
            },
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Stadt nicht gefunden</h1>
      <Link to="/photographers" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">
        Alle Fotografen ansehen
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: CityPage,
});

function CityPage() {
  const { t } = useTranslation();
  const { city } = Route.useLoaderData() as { city: CityInfo };
  const [all, setAll] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchAllPhotographers().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const matching = all.filter(
    (p) => photographerMatchesCity(p.city, city.name) || photographerMatchesCity(p.region, city.name),
  );

  // Nearby cities in same country for internal linking
  const nearby = cities.filter((c) => c.country === city.country && c.slug !== city.slug).slice(0, 8);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">Fotograf finden · {city.country}</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
            Fotograf in {city.name}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl">
            {cityIntro(city)}
          </p>
          <div className="mt-8 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            {themes.map((t) => (
              <Link
                key={t.id}
                to="/fotograf/$city/$theme"
                params={{ city: city.slug, theme: t.id }}
                className="hover:text-foreground"
              >
                {t.label} in {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">
            Fotografen in {city.name}
          </h2>
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{t("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="space-y-6 max-w-xl">
              <p className="text-muted-foreground">
                Noch keine gelisteten Fotografen in {city.name}. Wir empfehlen Fotografen aus
                dem nahen Umkreis, sieh dir unsere gesamte Auswahl an oder sende eine Anfrage, wir
                schlagen passende Fotografen für {city.name} vor.
              </p>
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link to="/photographers" className="border-b border-foreground pb-1">
                  Alle Fotografen
                </Link>
                <Link to="/brief" className="border-b border-foreground pb-1">
                  Brief senden
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {matching.map((p) => (
                <Link key={p.id} to="/photographers/$id" params={{ id: p.id }} className="block group cursor-pointer">
                  <PortraitImage p={p} />
                  <div className="mt-4 flex justify-between items-baseline">
                    <h3 className="serif text-2xl">{p.name}</h3>
                    <div className="eyebrow text-muted-foreground">{p.city ?? p.region}</div>
                  </div>
                  <div className="mt-2 eyebrow text-muted-foreground">
                    {p.themes.map((t) => themes.find((th) => th.id === t)?.label).join(" · ")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-6 font-normal">
            Weitere Städte in {city.country}
          </h2>
          <div className="flex flex-wrap gap-x-6 gap-y-3 serif text-xl">
            {nearby.map((c) => (
              <Link key={c.slug} to="/fotograf/$city" params={{ city: c.slug }} className="hover:underline">
                Fotograf {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-6 font-normal">So funktioniert OM Selected</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl">
            <div>
              <div className="serif text-2xl">Kuratiert</div>
              <p className="text-sm text-muted-foreground mt-2">
                Jede Fotograf wird redaktionell geprüft. Kein offener Marktplatz.
              </p>
            </div>
            <div>
              <div className="serif text-2xl">Lokal in {city.name}</div>
              <p className="text-sm text-muted-foreground mt-2">
                Fotografen mit Sitz in {city.name} oder regelmäßiger Verfügbarkeit vor Ort.
              </p>
            </div>
            <div>
              <div className="serif text-2xl">Direkt anfragen</div>
              <p className="text-sm text-muted-foreground mt-2">
                Direktkontakt zur Fotograf, oder <Link to="/brief" className="underline">Brief senden</Link>, wir schlagen passende Profile vor.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}